import config from 'config';
import Logger from 'utils/logger';
import { IntercomUser } from 'types/User';
import { isDevelopmentMode } from 'utils/helpers';

const IntercomUtils = {
  shouldSkip() {
    return !config.INTERCOM_APP_ID || isDevelopmentMode();
  },
  isAvailable() {
    return !this.shouldSkip() && typeof window.Intercom === 'function';
  },
  loadScript() {
    try {
      if (!config.INTERCOM_APP_ID) {
        Logger.log('Skipping Intercom script load');
        return;
      }

      if (typeof window.Intercom === 'function') {
        window.Intercom('reattach_activator');
        window.Intercom('update', window.intercomSettings);
        return;
      }

      const i = function (...args: unknown[]) {
        (i as any).c(args);
      } as any;
      i.q = [] as unknown[][];
      i.c = function (args: unknown[]) {
        i.q.push(args);
      };
      window.Intercom = i;

      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://widget.intercom.io/widget/' + config.INTERCOM_APP_ID;
      const x = document.getElementsByTagName('script')[0];
      x?.parentNode?.insertBefore(s, x);
    } catch (e) {
      Logger.error(e);
    }
  },
  init(user: IntercomUser) {
    try {
      if (this.shouldSkip()) {
        Logger.log('Skipping Intercom initialization');
        return;
      }

      if (typeof window.Intercom === 'function') {
        return window.Intercom('boot', {
          ...user,
          avatar: {
            ...user.avatar,
            type: 'avatar',
          },
          app_id: config.INTERCOM_APP_ID,
        });
      }
    } catch (e) {
      Logger.error(e);
    }
  },
  newConversation(message: string) {
    try {
      if (this.shouldSkip()) return;

      if (typeof window.Intercom === 'function') {
        return window.Intercom('showNewMessage', message);
      }
    } catch (e) {
      Logger.error(e);
    }
  },
  track(eventName: string) {
    try {
      if (this.shouldSkip()) {
        Logger.info('Track intercom event: %s', eventName);
        return;
      }

      if (typeof window.Intercom === 'function') {
        return window.Intercom('trackEvent', eventName);
      }
    } catch (e) {
      Logger.error(e);
    }
  },

  events: {
    POST_NOTE_FIELD_FOCUSED: 'post field note focused',
    POST_LABELS_SELECT_FOCUSED: 'post labels input focused',
    POSTS_TIME_SAVERS_DUPLICATE: 'posts time savers duplicate posts',
    POSTS_TIME_SAVERS_MOVE: 'posts time savers move posts',
    POSTS_TIME_SAVERS_MOVE_TO_TRASH: 'posts time savers move to trash',
    POSTS_TIME_SAVERS_CHANGE_STATUS: 'posts time savers change posts status',
    POSTS_TIME_SAVERS_ASSIGN: 'posts time savers assign multiple tasks',
    POSTS_TIME_SAVERS_CLIENT_APPROVAL:
      'posts time savers send for client approval',
    POSTS_TIME_SAVERS_INTERNAL_APPROVAL:
      'posts time savers send for internal approval',
    POSTS_TIME_SAVERS_SCHEDULE: 'posts time savers schedule posts',
    POST_DETAIL_DUPLICATE_POST: 'post detail duplicate post',
  },
};

export default IntercomUtils;
